// #1861: Dennis' view of the coaching subscriptions (layout after the concept
// mockup v1): totals, group, customers with allowance bars and warnings
// (80/100 %), record time (chat 5/15 min, correction with reason) and all rules
// and tiers as editable settings (start values Dennis 04.11.2026).
import { coachingAboEinstellungenSchema } from '@embertime/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import {
  api,
  type CoachingAboKontingent,
  type CoachingAboRegeln,
  type CoachingAboStufe,
  type CoachingAboUebersicht,
} from '../lib/api'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/coaching/abos',
  component: CoachingAbosPage,
})

type Abo = NonNullable<CoachingAboUebersicht['abos']>[number]
const euro = (cent: number) =>
  (cent / 100).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: cent % 100 ? 2 : 0,
  })
const datum = (d: string) =>
  new Date(d).toLocaleDateString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
  })
const STATUS: Record<string, string> = {
  aktiv: 'aktiv',
  gekuendigt: 'gekündigt',
  zahlung_offen: 'Zahlung offen',
  checkout: 'Checkout offen',
  beendet: 'beendet',
}
const RECHNUNG: Record<string, string> = {
  automatisch: 'emberfibu automatisch',
  monatsposition_fehlt: 'emberfibu: Monatsposition fehlt (manuell)',
  betrag_nicht_darstellbar: 'Betrag nicht in Stunden darstellbar',
  firma_fehlt: 'Firma fehlt',
}

function Bar({ k }: { k: CoachingAboKontingent }) {
  const total = Math.max(k.gesamt, k.verbraucht, 1)
  const pct = (n: number) => `${(n / total) * 100}%`
  return (
    <div
      className="flex h-2.5 w-full overflow-hidden rounded-full bg-bg-overlay"
      role="img"
      aria-label={`${k.genutzt} genutzt, ${k.geplant} reserviert, ${k.rest} frei von ${k.gesamt} Minuten`}
    >
      <div
        className={k.ueberschreitung ? 'bg-danger' : 'bg-accent'}
        style={{ width: pct(k.genutzt) }}
      />
      <div
        className="bg-[repeating-linear-gradient(135deg,rgb(var(--c-accent)/0.8)_0_3px,rgb(var(--c-accent)/0.25)_3px_6px)]"
        style={{ width: pct(k.geplant) }}
      />
    </div>
  )
}

function Hinweis({ abo }: { abo: Abo }) {
  if (abo.status === 'zahlung_offen')
    return (
      <span className="rounded-full bg-danger/15 px-2 py-0.5 text-xs text-danger">
        Zahlung offen
      </span>
    )
  if (abo.warnung >= 100)
    return (
      <span className="rounded-full bg-danger/15 px-2 py-0.5 text-xs text-danger">
        aufgebraucht
      </span>
    )
  if (abo.warnung >= 80)
    return (
      <span className="rounded-full bg-warning/15 px-2 py-0.5 text-xs text-warning">
        Über {abo.warnung} % genutzt
      </span>
    )
  if (abo.hinweiseRechnung)
    return (
      <span className="rounded-full bg-warning/15 px-2 py-0.5 text-xs text-warning">
        Rechnung prüfen
      </span>
    )
  return (
    <span className="rounded-full bg-success/15 px-2 py-0.5 text-xs text-success">Im Rahmen</span>
  )
}

function ZeitErfassen({
  abo,
  regeln,
  onClose,
}: {
  abo: Abo
  regeln: CoachingAboRegeln
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [art, setArt] = useState<'chat_kurz' | 'chat_lang' | 'korrektur'>('chat_lang')
  const [minuten, setMinuten] = useState('')
  const [notiz, setNotiz] = useState('')
  const [requestId, setRequestId] = useState(() => crypto.randomUUID())
  const m = useMutation({
    mutationFn: () =>
      api.coachingAboVerbrauch(abo.id, {
        requestId,
        art,
        minuten: art === 'korrektur' ? Number(minuten) : undefined,
        notiz,
      }),
    onSuccess: (r) => {
      notify.success(`${r.minuten} Min. gebucht`)
      setRequestId(crypto.randomUUID())
      qc.invalidateQueries({ queryKey: ['coaching-abo'] })
      onClose()
    },
    onError: (e: Error) => notify.error(e.message),
  })
  const min =
    art === 'chat_kurz'
      ? regeln.chatKurzMin
      : art === 'chat_lang'
        ? regeln.chatLangMin
        : Number(minuten) || 0
  const k = abo.kontingent
  return (
    <section className="space-y-3 rounded-2xl border border-border-subtle bg-bg-elevated/40 p-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-accent">
        Zeit erfassen · {abo.name}
      </p>
      <h3 className="text-lg font-semibold text-text-primary">Nachvollziehbar buchen</h3>
      <fieldset className="flex flex-wrap gap-2" aria-label="Art">
        {(
          [
            ['chat_kurz', `Kurzfrage ${regeln.chatKurzMin} Min.`],
            ['chat_lang', `Ausführlich ${regeln.chatLangMin} Min.`],
            ['korrektur', 'Korrektur ±'],
          ] as const
        ).map(([v, l]) => (
          <Button
            key={v}
            type="button"
            size="sm"
            variant={art === v ? 'primary' : 'secondary'}
            aria-pressed={art === v}
            onClick={() => setArt(v)}
          >
            {l}
          </Button>
        ))}
      </fieldset>
      {art === 'korrektur' && (
        <div>
          <Label htmlFor="abo-min">Minuten (negativ = Gutschrift)</Label>
          <Input
            id="abo-min"
            inputMode="numeric"
            value={minuten}
            onChange={(e) => setMinuten(e.target.value)}
          />
        </div>
      )}
      <div>
        <Label htmlFor="abo-notiz">Kundentext (sichtbar im Verlauf)</Label>
        <Input
          id="abo-notiz"
          maxLength={200}
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
          placeholder="z. B. Prompt geprüft"
        />
      </div>
      {k && (
        <p className="rounded-xl bg-bg-overlay px-3 py-2 text-sm text-text-secondary">
          Vorschau: {k.genutzt + min} genutzt · {k.geplant} reserviert · {Math.max(0, k.rest - min)}{' '}
          frei. Pauschale vorher mit dem Kunden bestätigen.
        </p>
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          disabled={m.isPending || (art === 'korrektur' && (!Number(minuten) || !notiz))}
          onClick={() => m.mutate()}
        >
          {min} Min. verbindlich buchen
        </Button>
        <Button type="button" variant="secondary" onClick={onClose}>
          Verwerfen
        </Button>
      </div>
      <p className="text-xs text-text-tertiary">
        Korrektur = Gegenbuchung mit Grund. Keine stillen Änderungen.
      </p>
    </section>
  )
}

function Einstellungen({ data }: { data: CoachingAboUebersicht }) {
  const qc = useQueryClient()
  const [r, setR] = useState<CoachingAboRegeln | null>(null)
  const [warnText, setWarnText] = useState('')
  const [dauerText, setDauerText] = useState('')
  const [stufen, setStufen] = useState<CoachingAboStufe[]>([])
  useEffect(() => {
    if (data.einstellungen) {
      setR(data.einstellungen)
      setWarnText(data.einstellungen.warnSchwellen.join(', '))
      setDauerText(data.einstellungen.terminDauern.join(', '))
    }
    if (data.stufen) setStufen(data.stufen)
  }, [data.einstellungen, data.stufen])
  const save = useMutation({
    mutationFn: () => {
      if (!r || !data.bereich) throw new Error('Kein Bereich')
      const { konfiguriert: _k, ...regeln } = r
      return api.coachingAboEinstellungen(
        coachingAboEinstellungenSchema.parse({
          areaId: data.bereich.id,
          ...regeln,
          warnSchwellen: warnText.trim()
            ? warnText
                .trim()
                .split(/[,\s]+/)
                .map(Number)
            : [],
          terminDauern: dauerText.trim()
            ? dauerText
                .trim()
                .split(/[,\s]+/)
                .map(Number)
            : [],
          stufen: stufen.map(({ nettoJeStundeCent: _n, rechnung: _r, ...s }) => ({
            ...s,
            maxTeilnehmer: s.art === 'gruppe' ? (s.maxTeilnehmer ?? 10) : null,
          })),
        }),
      )
    },
    onSuccess: () => {
      notify.success('Einstellungen gespeichert')
      qc.invalidateQueries({ queryKey: ['coaching-abo'] })
    },
    onError: (e: Error) => notify.error(e.message),
  })
  if (!r) return null
  const num = (key: keyof CoachingAboRegeln, label: string, hint?: string) => (
    <div>
      <Label htmlFor={`r-${key}`}>{label}</Label>
      <Input
        id={`r-${key}`}
        inputMode="numeric"
        value={String(r[key])}
        onChange={(e) => setR({ ...r, [key]: Number(e.target.value) || 0 })}
      />
      {hint && <p className="mt-1 text-xs text-text-tertiary">{hint}</p>}
    </div>
  )
  const setStufe = (i: number, patch: Partial<CoachingAboStufe>) =>
    setStufen(stufen.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  return (
    <section className="space-y-4 rounded-2xl border border-border-subtle bg-bg-elevated/40 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-text-primary">Regeln, Pakete und Preise</h2>
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${r.platzhalter ? 'bg-warning/15 text-warning' : 'bg-success/15 text-success'}`}
        >
          {r.platzhalter
            ? r.konfiguriert
              ? 'Entwurf, nicht freigegeben'
              : 'Startwerte Dennis 04.11. (ungespeichert)'
            : 'freigegeben'}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {num(
          'zeitwertNettoCentJeMin',
          'Zeitwert netto (Cent/Min.)',
          `${euro(r.zeitwertNettoCentJeMin * 60)} netto/h`,
        )}
        {num('einheitMin', 'Einheit (Min.)', r.aufrunden ? 'aufgerundet' : 'kaufmännisch')}
        {num('chatKurzMin', 'Kurzfrage (Min.)')}
        {num('chatLangMin', 'Ausführliche Frage (Min.)')}
        {num('stornoStunden', 'Stornofrist (h)')}
        {num('mindestlaufzeitMonate', 'Mindestlaufzeit (Monate)')}
        {num('uebertragMaxProzent', 'Übertrag max. (% der kleineren Ration)')}
        <div>
          <Label htmlFor="r-warn">Warnschwellen (%)</Label>
          <Input id="r-warn" value={warnText} onChange={(e) => setWarnText(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="r-dauern">Termindauern (Min.)</Label>
          <Input id="r-dauern" value={dauerText} onChange={(e) => setDauerText(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={r.aufrunden}
            onChange={(e) => setR({ ...r, aufrunden: e.target.checked })}
          />{' '}
          Aufrunden
        </label>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={r.uebertragMonate === 1}
            onChange={(e) => setR({ ...r, uebertragMonate: e.target.checked ? 1 : 0 })}
          />{' '}
          Übertrag 1 Monat
        </label>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={r.ueberschreitung === 'nachberechnen'}
            onChange={(e) =>
              setR({ ...r, ueberschreitung: e.target.checked ? 'nachberechnen' : 'sperren' })
            }
          />
          Überziehung erlauben (sonst sperren)
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-text-tertiary">
              <th className="p-2">Paket</th>
              <th className="p-2">Typ</th>
              <th className="p-2">Brutto (Cent)</th>
              <th className="p-2">Min./Plätze</th>
              <th className="p-2">netto/h</th>
              <th className="p-2">Rechnung</th>
              <th className="p-2">aktiv</th>
            </tr>
          </thead>
          <tbody>
            {stufen.map((s, i) => (
              <tr key={s.slug} className="border-t border-border-subtle">
                <td className="p-2">
                  <Input
                    aria-label={`Name ${s.slug}`}
                    value={s.name}
                    onChange={(e) => setStufe(i, { name: e.target.value })}
                  />
                </td>
                <td className="p-2 text-text-secondary">{s.art}</td>
                <td className="p-2">
                  <Input
                    aria-label={`Preis ${s.slug}`}
                    inputMode="numeric"
                    value={String(s.preisCent)}
                    onChange={(e) => setStufe(i, { preisCent: Number(e.target.value) || 0 })}
                  />
                </td>
                <td className="p-2">
                  {s.art === 'gruppe' ? (
                    <Input
                      aria-label={`Plätze ${s.slug}`}
                      inputMode="numeric"
                      value={String(s.maxTeilnehmer ?? 10)}
                      onChange={(e) => setStufe(i, { maxTeilnehmer: Number(e.target.value) || 10 })}
                    />
                  ) : (
                    <Input
                      aria-label={`Minuten ${s.slug}`}
                      inputMode="numeric"
                      value={String(s.minuten)}
                      onChange={(e) => setStufe(i, { minuten: Number(e.target.value) || 0 })}
                    />
                  )}
                </td>
                <td className="p-2 text-text-secondary">
                  {s.nettoJeStundeCent ? euro(s.nettoJeStundeCent) : '–'}
                </td>
                <td className="p-2 text-xs text-text-tertiary">
                  {RECHNUNG[s.rechnung] ?? s.rechnung}
                </td>
                <td className="p-2">
                  <input
                    type="checkbox"
                    aria-label={`aktiv ${s.slug}`}
                    checked={s.aktiv}
                    onChange={(e) => setStufe(i, { aktiv: e.target.checked })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
          Speichern
        </Button>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={!r.platzhalter}
            onChange={(e) => setR({ ...r, platzhalter: !e.target.checked })}
          />{' '}
          Regeln freigegeben (Hinweis „Entwurf“ ausblenden)
        </label>
      </div>
      <p className="text-xs text-text-tertiary">
        Preise brutto inkl. 19 % USt. Eine Preisänderung gilt für neue Abschlüsse (neuer
        Stripe-Preis), laufende Abos behalten ihren Preis. Pakete werden nie gelöscht, nur
        deaktiviert.
      </p>
    </section>
  )
}

function Gruppe({ data }: { data: CoachingAboUebersicht }) {
  const qc = useQueryClient()
  const gruppe = data.stufen?.find((s) => s.art === 'gruppe')
  const mitglieder =
    data.abos?.filter((a) => a.art === 'gruppe' && a.status !== 'beendet').length ?? 0
  const [titel, setTitel] = useState('Gruppen-Coaching')
  const [start, setStart] = useState('')
  const [dauer, setDauer] = useState('60')
  const [url, setUrl] = useState('')
  const m = useMutation({
    mutationFn: () =>
      api.coachingGruppentermin({
        areaId: data.bereich?.id,
        titel,
        start: new Date(start).toISOString(),
        dauerMin: Number(dauer),
        maxTeilnehmer: gruppe?.maxTeilnehmer ?? 10,
        zugangUrl: url || null,
      }),
    onSuccess: () => {
      notify.success('Gruppentermin angelegt')
      qc.invalidateQueries({ queryKey: ['coaching-abo'] })
    },
    onError: (e: Error) => notify.error(e.message),
  })
  if (!gruppe) return null
  return (
    <section className="space-y-3 rounded-2xl border border-border-subtle bg-bg-elevated/40 p-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-accent">
        Gruppe · eigener Angebotstyp
      </p>
      <h2 className="text-lg font-semibold text-text-primary">
        {mitglieder} von {gruppe.maxTeilnehmer ?? 10} Plätzen belegt
      </h2>
      <p className="text-sm text-text-secondary">
        {euro(gruppe.preisCent)} pro Person / Monat · Material inklusive · keine Minutenkonten.
        Dauer und Wochenzeit legst du je Termin fest.
      </p>
      <ul className="divide-y divide-border-subtle text-sm">
        {(data.gruppentermine ?? []).map((g) => (
          <li key={g.id} className="flex justify-between gap-2 py-2">
            <span className={g.abgesagt ? 'text-text-tertiary line-through' : 'text-text-primary'}>
              {new Date(g.start).toLocaleString('de-DE', {
                timeZone: 'Europe/Berlin',
                weekday: 'short',
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}{' '}
              · {g.titel} · {g.dauerMin} Min.
            </span>
            <span className="text-text-secondary">
              {g.belegt}/{g.maxTeilnehmer}
            </span>
          </li>
        ))}
      </ul>
      <div className="grid gap-2 sm:grid-cols-4">
        <Input aria-label="Titel" value={titel} onChange={(e) => setTitel(e.target.value)} />
        <Input
          aria-label="Beginn"
          type="datetime-local"
          value={start}
          onChange={(e) => setStart(e.target.value)}
        />
        <Input
          aria-label="Dauer"
          inputMode="numeric"
          value={dauer}
          onChange={(e) => setDauer(e.target.value)}
        />
        <Input
          aria-label="Raum-Link (https)"
          placeholder="https://… Raum-Link"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>
      <Button
        type="button"
        variant="secondary"
        disabled={!start || m.isPending}
        onClick={() => m.mutate()}
      >
        Gruppentermin anlegen
      </Button>
    </section>
  )
}

function CoachingAbosPage() {
  const me = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.me() })
  const q = useQuery({
    queryKey: ['coaching-abo'],
    queryFn: () => api.coachingAbo(),
    enabled: me.data?.user?.role === 'admin',
  })
  const [erfassen, setErfassen] = useState<string | null>(null)
  if (me.isLoading) return null
  if (!me.data?.user) return <Navigate to="/login" />
  if (me.data.user.role !== 'admin')
    return <p className="p-8 text-text-secondary">Coaching-Abos sind nur für Admins sichtbar.</p>
  const d = q.data
  const abos = d?.abos ?? []
  const minutenAbos = abos.filter((a) => a.kontingent)
  const summe = minutenAbos.reduce(
    (s, a) => ({
      genutzt: s.genutzt + (a.kontingent?.genutzt ?? 0),
      geplant: s.geplant + (a.kontingent?.geplant ?? 0),
      gesamt: s.gesamt + (a.kontingent?.gesamt ?? 0),
      rest: s.rest + (a.kontingent?.rest ?? 0),
    }),
    { genutzt: 0, geplant: 0, gesamt: 0, rest: 0 },
  )
  const aktive = abos.filter((a) => ['aktiv', 'gekuendigt', 'zahlung_offen'].includes(a.status))
  const hinweise = abos.filter(
    (a) => a.warnung >= 80 || a.hinweiseRechnung || a.status === 'zahlung_offen' || a.wunschStufe,
  )
  const zaehle = (art: string) => aktive.filter((a) => a.stufe === art).length
  const offen = abos.find((a) => a.id === erfassen)
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-accent">
          Dennis · Coaching-Verwaltung
        </p>
        <h1 className="text-2xl font-bold text-text-primary">Auslastung mit Überblick.</h1>
      </div>
      {q.isLoading && <p className="text-text-secondary">Lade …</p>}
      {d && !d.bereich && (
        <p className="text-text-secondary">Kein KI-Coaching-Bereich eingerichtet.</p>
      )}
      {d?.bereich && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-5">
              <p className="font-semibold text-text-primary">Aktive Abos</p>
              <p className="text-3xl font-bold text-text-primary">{aktive.length}</p>
              <p className="text-xs text-text-tertiary">
                {(d.stufen ?? [])
                  .filter((s) => s.art !== 'nachkauf')
                  .map((s) => `${zaehle(s.slug)} ${s.name}`)
                  .join(' · ')}
              </p>
            </div>
            <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-5">
              <p className="font-semibold text-text-primary">1:1 im laufenden Zeitraum</p>
              <p className="text-3xl font-bold text-text-primary">
                {summe.genutzt} / {summe.gesamt} Min.
              </p>
              <p className="text-xs text-text-tertiary">
                {summe.geplant} Min. reserviert · {summe.rest} Min. frei
              </p>
            </div>
            <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-5">
              <p className="font-semibold text-text-primary">Prüfbedarf</p>
              <p
                className={`text-3xl font-bold ${hinweise.length ? 'text-accent' : 'text-text-primary'}`}
              >
                {hinweise.length} {hinweise.length === 1 ? 'Hinweis' : 'Hinweise'}
              </p>
              <p className="text-xs text-text-tertiary">
                Warnungen 80/100 %, Zahlungen, Rechnungen, Wechselwünsche
              </p>
            </div>
          </div>
          <Gruppe data={d} />
          <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-5">
            <h2 className="mb-2 text-lg font-semibold text-text-primary">
              Kunden &amp; Kontingente
            </h2>
            {abos.length === 0 && <p className="text-sm text-text-secondary">Noch keine Abos.</p>}
            <ul className="divide-y divide-border-subtle">
              {abos.map((a) => (
                <li
                  key={a.id}
                  className="grid gap-3 py-4 md:grid-cols-[1.1fr_1.6fr_1.2fr_auto] md:items-center"
                >
                  <div>
                    <p className="font-semibold text-text-primary">{a.name}</p>
                    <p className="text-xs text-text-secondary">
                      {(d.stufen ?? []).find((s) => s.slug === a.stufe)?.name ?? a.stufe} ·{' '}
                      {euro(a.preisCent)} / Monat · {STATUS[a.status] ?? a.status}
                    </p>
                  </div>
                  <div>
                    {a.kontingent ? (
                      <>
                        <p className="text-sm font-semibold text-text-primary">
                          {a.kontingent.genutzt} von {a.kontingent.gesamt} Min. genutzt
                        </p>
                        <Bar k={a.kontingent} />
                        <p className="text-xs text-text-tertiary">
                          {a.kontingent.geplant} reserviert · {a.kontingent.rest} frei
                          {a.kontingent.uebertrag ? ` · ${a.kontingent.uebertrag} Übertrag` : ''}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-text-secondary">
                        {a.art === 'gruppe'
                          ? 'Gruppe · Teilnahme statt Minuten'
                          : 'Kein bezahlter Zeitraum'}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 text-xs text-text-secondary">
                    <Hinweis abo={a} />
                    <p>
                      {a.kontingent ? `Erneuerung ${datum(a.kontingent.bis)}` : ''}
                      {a.kuendigungZum ? ` · endet ${datum(a.kuendigungZum)}` : ''}
                      {a.wunschStufe ? ` · Wechselwunsch: ${a.wunschStufe}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {a.art === 'abo' && a.kontingent && (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => setErfassen(a.id)}
                      >
                        Zeit erfassen
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
          {offen && d.einstellungen && (
            <ZeitErfassen abo={offen} regeln={d.einstellungen} onClose={() => setErfassen(null)} />
          )}
          <Einstellungen data={d} />
        </>
      )}
    </div>
  )
}
